import {
  getActiveIntent,
  getPrimaryIntent,
  getSecondaryIntents,
  mapIntentToTab,
} from '@pano-project/utils/intentMapping';
import { rememberIntentSwitch } from '@pano-project/utils/intentSwitchMemory';
import { removeHashParams } from 'utils/helper-functions/remove-hash-params';
import { hasTriedToSubmit } from '@pano-project/utils/submissionAttempt';
import { isActiveTabEmptyOnAmountFirst } from '@pano-project/utils/intentAvailability';
import {
  canRequestGatewaysOnAmountFirst,
  isOBRecoveryScenarioOf,
  isRetryModeOf,
} from '@pano-project/utils/gatewaysFetchCondition';
import { RecoverySettingsModel } from 'utils/models/RecoverySettings.Model';
import { initialDataPrefetch } from '../../shared-components/wp-components/initial-data/initial-data-prefetch';
import { resetSelectedBonus } from './selected-bonus-actions';
import { clearErrors } from './errors-actions';
import { changePayData } from './pay-data-actions';
import { tabSwitchGatewaysSettled } from './tab-switch-lock-actions';
import { changeAmountFirstStep, changeInitTheme, getCustomerData } from './index';
import { ResponseStatus } from '../../variables/response-status';

/**
 * Throws away everything the player entered or chose on the tab being left:
 * amount, currency, payment method, gateway, entered fields, chosen template,
 * promo code and validation errors. The reducers that own each of those pieces
 * handle this one action; the PCI gate and the customer's stored cards are
 * deliberately untouched because they belong to the session, not to the tab.
 */
export const resetTabState = () => ({ type: 'RESET_TAB_STATE' });

/**
 * Drops the previous tab's decline recovery settings from the address bar, so a
 * later refresh of this browser tab does not reopen that recovery. The copy in
 * application state is dropped by the RESET_TAB_STATE reducer.
 */
export const clearRecoverySettingsFromAddress = () => {
  try {
    if (!window.location.hash.includes('recoverySettings=')) return;
    window.history.replaceState(window.history.state, '', removeHashParams(window.location.href));
  } catch (err) {
    // The address bar could not be rewritten; the state copy is still cleared.
  }
};

/**
 * Writes the intent of the tab the player is now on. It lives at the top level
 * of initTheme on purpose: that reducer merges top-level keys but replaces the
 * nested session object wholesale, so writing it here cannot erase the balance
 * or the currency.
 */
export const setActiveIntent = (intent) => changeInitTheme({ activeIntent: intent });

/**
 * The session intent a tab identifier stands for, or undefined when the session
 * was not created with an intent for that tab.
 */
export const resolveIntentForTab = (state, tab) =>
  [getPrimaryIntent(state), ...getSecondaryIntents(state)].find(
    (intent) => mapIntentToTab(intent) === tab,
  );

/**
 * Re-applies the values the merchant pre-set when creating the session.
 *
 * They belong to the whole session rather than to one tab, so the tab switch
 * clears them with the rest of the previous tab's state and then puts them back
 * on the newly opened tab. Writing the amount and the currency back into the
 * pay data is what makes the tab's own limit and balance validation run against
 * them again; the boot-time fatal check is deliberately not repeated, because a
 * balance problem must stay an inline error rather than take the tab away.
 */
export const applySessionPresetValues = () => (dispatch, getState) => {
  const state = getState();
  const sessionInformation = state?.initTheme?.sessionInformation || {};
  const presetAmount = sessionInformation.amount;
  const presetCurrency = sessionInformation.currency || state?.profileInfo?.base_currency;

  const presetPayData = {
    ...(presetAmount !== undefined &&
      presetAmount !== null &&
      presetAmount !== '' && { amount: presetAmount }),
    ...(presetCurrency && { currency: presetCurrency }),
  };

  if (Object.keys(presetPayData).length) dispatch(changePayData(presetPayData));

  return presetPayData;
};

/**
 * Whether a gateways request can still follow a finished switch, which decides
 * if the tabs wait for a response or unlock at once.
 *
 * None can follow when the customer data of the new tab did not load or says
 * the tab is empty (any reply that is not OK), nor on Amount-first while the
 * condition of its fetch is not met (no amount yet, or a tab known to be
 * empty). The Panoramic layout always asks for the list of a loaded tab.
 */
export const canGatewaysRequestFollowSwitch = (state, customerData) => {
  if (customerData?.status !== ResponseStatus.OK) return false;

  const layout = state?.initTheme?.theme_editor_settings?.customization_settings?.layout;
  if (layout !== 'amount-first') return true;

  return canRequestGatewaysOnAmountFirst({
    amount: state?.payData?.amount,
    currency: state?.payData?.currency,
    isOBRecoveryScenario: isOBRecoveryScenarioOf(
      RecoverySettingsModel.getRecoverySettings(),
      isRetryModeOf(state?.resultOfPay),
    ),
    isActiveTabEmpty: isActiveTabEmptyOnAmountFirst(state, getActiveIntent(state)),
  });
};

// Ends the switch's wait when it is still on and no gateways request can follow.
// A request that settled meanwhile has ended the wait already.
const releaseTabsIfNoGatewaysRequestFollows = (dispatch, getState, customerData) => {
  const state = getState();

  if (!state?.tabSwitchAwaitingGateways) return;
  if (canGatewaysRequestFollowSwitch(state, customerData)) return;

  dispatch(tabSwitchGatewaysSettled());
};

/**
 * Switches the player to another tab: sets the new active intent, clears the
 * previous tab's state and reloads the data that belongs to the new tab.
 *
 * Resolves with false when there is nothing to switch to — the tab the player
 * is already on, or a tab this session was not created with.
 *
 * Both tabs are locked from the reset on until the gateways request that
 * follows the switch settles (see fetchGateways), or until the switch has
 * loaded and no such request can follow (see canGatewaysRequestFollowSwitch).
 */
export const switchTransactionTab = (tab) => (dispatch, getState) => {
  const state = getState();

  // Once the player has tried to submit, the transaction of the current tab is
  // under way or answered, so no other tab is offered (see hasTriedToSubmit).
  if (hasTriedToSubmit(state)) return Promise.resolve(false);

  const nextIntent = resolveIntentForTab(state, tab);

  if (!nextIntent || nextIntent === getActiveIntent(state)) return Promise.resolve(false);

  // The head-start data describes the tab the session opened on, so no request
  // made for the new tab may be served from it.
  initialDataPrefetch.clear();

  // Clears the previous tab's entered data and locks both tabs (see the
  // tabSwitchAwaitingGateways reducer). The reducers still clear a stored
  // submission reply, decline recovery settings and the partial-approval flag as
  // a safeguard, although a switch is refused once any of them is set.
  dispatch(resetTabState());
  clearRecoverySettingsFromAddress();
  dispatch(clearErrors());
  dispatch(resetSelectedBonus());
  dispatch(changeAmountFirstStep(1));

  dispatch(setActiveIntent(nextIntent));
  // From now on every request carries the active tab's value, on both tabs,
  // and a reload of this browser tab reopens the tab the player is on.
  rememberIntentSwitch(nextIntent);

  // The switch itself asks for no payment method list on either layout. The
  // panoramic layout reloads the list from its own loader as soon as the
  // transaction type it watches changes; the amount-first layout asks for it
  // only once the new tab has an amount, entered by the player or filled in
  // from the tab's last transaction or the session's pre-set amount.
  return dispatch(getCustomerData())
    .then((customerData) => {
      dispatch(applySessionPresetValues());
      releaseTabsIfNoGatewaysRequestFollows(dispatch, getState, customerData);
      return true;
    })
    .catch((error) => {
      releaseTabsIfNoGatewaysRequestFollows(dispatch, getState);
      throw error;
    });
};
